import { DevicePlatform, ModGeneration, SoftwareVersion } from '../enums';
import { IVdbsResponse, VdbsClient } from './VdbsClient';
import { LoggingFacade } from '@vwdfive/myvw-logging';

export class VdbsService {

    constructor(private readonly vdbsClient: VdbsClient, private readonly loggingFacade: LoggingFacade) {}

    public async getPlatformDetails(vin: string | undefined, softwareVersion = false, modGeneration = false): Promise<IVdbsResponse> {
        let vdbsResponse: IVdbsResponse = { acronym: DevicePlatform.UNKNOWN, softwareVersion: '', modGeneration: ModGeneration.UNKNOWN };

        if (!vin) {
            return vdbsResponse;
        }

        vdbsResponse = await this.vdbsClient.callVdbsDevicePlatformEndpoint(vin, softwareVersion, modGeneration);

        const devicePlatform = DevicePlatform[vdbsResponse?.acronym as keyof typeof DevicePlatform];
        if (!devicePlatform) {
            this.loggingFacade.error(`Could not fetch devicePlatform from VDBS for VIN ${vin}`);
            vdbsResponse.acronym = DevicePlatform.UNKNOWN;
        }

        if (softwareVersion && vdbsResponse.acronym !== DevicePlatform.WCAR) {
            this.loggingFacade.error(`Could not fetch softwareVersion from VDBS for VIN ${vin} and Device Platform ${vdbsResponse.acronym}`);
            vdbsResponse.softwareVersion = '';
        }

        if (modGeneration && !vdbsResponse?.modGeneration) {
            this.loggingFacade.error(`Could not fetch modGeneration from VDBS for VIN ${vin}`);
            vdbsResponse.modGeneration = ModGeneration.UNKNOWN;
        }

        return vdbsResponse;
    }

    /**
     * @deprecated The method should not be used, see  {@link getPlatformDetails}
     */
    public async getDevicePlatform(vin: string | undefined): Promise<DevicePlatform> {
        if (!vin) {
            return DevicePlatform.UNKNOWN;
        }
        const vdbsResponse = await this.vdbsClient.getDevicePlatform(vin);
        const devicePlatform = DevicePlatform[vdbsResponse?.acronym as keyof typeof DevicePlatform];
        if (!devicePlatform) {
            this.loggingFacade.error(`Could not fetch devicePlatform from VDBS for VIN ${vin}`);
        }
        return devicePlatform ?? DevicePlatform.UNKNOWN;
    }

    public async getWcarSoftwareVersion(vin: string, devicePlatform: DevicePlatform): Promise<SoftwareVersion> {
        if (devicePlatform !== DevicePlatform.WCAR) {
            return SoftwareVersion.UNKNOWN;
        }
        const vdbsResponse = await this.vdbsClient.getDevicePlatformWithOnlineDevices(vin);

        return this.getSoftwareVersionEnum(vdbsResponse?.softwareVersion ?? SoftwareVersion.UNKNOWN);
    }

    public async getSoftwareVersion(vin: string, devicePlatform: DevicePlatform): Promise<String> {
        if (devicePlatform !== DevicePlatform.WCAR) {
            return '';
        }
        const vdbsResponse = await this.vdbsClient.getDevicePlatformWithOnlineDevices(vin);

        return vdbsResponse?.softwareVersion || '';
    }

    public async getModGeneration(vin: string | undefined): Promise<ModGeneration> {
        if (!vin) {
            return ModGeneration.UNKNOWN;
        }
        const vdbsResponse = await this.vdbsClient.getModGeneration(vin);
        if (!vdbsResponse?.modGeneration || !Object.values(ModGeneration).includes(vdbsResponse.modGeneration)) {            
            return ModGeneration.UNKNOWN;
        }
        if (vdbsResponse?.modGeneration && vdbsResponse?.modGeneration?.length) {
            return vdbsResponse.modGeneration;
        }
        return ModGeneration.UNKNOWN;
    }

    private getSoftwareVersionEnum(softwareVersion: string): SoftwareVersion {
        if (softwareVersion.startsWith('0.')) {
            return SoftwareVersion.ME0;
        }
        if (softwareVersion.startsWith('1.')) {
            return SoftwareVersion.ME1;
        }
        if (softwareVersion.startsWith('2.')) {
            return SoftwareVersion.ME2;
        }
        return SoftwareVersion.UNKNOWN;
    }
}
